import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const creditCards = {
    layout: {
        listTitle: 'Credit Cards',
        archivedTitle: 'Archived cards',
        newCardTitle: 'New Card',
        editCardTitle: 'Edit Card',
        invoiceTitle: 'Statement',
        reconcileTitle: 'Reconcile with statement',
        invoiceReportTitle: 'Statement report',
    },
    list: {
        screenTitle: 'My Cards',
        archivedButton: 'Archived cards',
        summary: {
            totalLabel: 'Total in statements',
            noCurrentInvoice: 'No current statement',
            trendNoCurrentInvoice: 'No current statement to highlight',
            trendNoBaseline: 'No baseline to compare with last month',
            trendSameLevel: 'Same level as last month',
            trendLower: '{{percent}} lower than last month',
            trendHigher: '{{percent}} higher than last month',
        },
        activeSection: 'Active cards',
        emptyTitle: 'No cards',
        emptySubtitleWithArchived: 'Tap the archived icon at the top to reactivate a card.',
        emptySubtitle: 'Add your first credit card',
        emptyAction: 'Add card',
        item: {
            subtitleFallback: 'Card',
            invoiceLabelOverdue: 'OVERDUE STATEMENT',
            invoiceLabelClosed: 'CLOSED STATEMENT',
            invoiceLabelOpen: 'OPEN STATEMENT',
            invoiceLabelEmpty: 'NO STATEMENT',
            openInvoice: 'Open: {{amount}}',
            closedInvoice: 'Due: {{amount}}',
            nextInvoice: 'Next: {{amount}}',
            dueOnDate: 'Due {{date}}',
            dueOnDay: 'Due on the {{day}}',
            noDueDate: 'No due date',
            freeLimit: 'Available limit: {{amount}}',
            dragHandle: 'Hold to reorder card',
        },
    },
    home: {
        helperNoOpenInvoice: 'No open statement',
        helperCurrentOverdue: 'Current was due {{date}}',
        helperClosing: 'Closes {{date}}',
        helperOverdue: 'Was due {{date}}',
    },
    archived: {
        fallbackSubtitle: 'Archived',
        reactivateButton: 'Reactivate',
        emptyTitle: 'No archived cards',
        emptySubtitle: 'Archived cards appear here for you to reactivate whenever you want.',
    },
    form: {
        nameLabel: 'Card name',
        namePlaceholder: 'e.g. Chase, Amex',
        brandOrInstitutionLabel: 'Card network or bank',
        brandOrInstitutionPlaceholder: 'Pick a network or bank',
        lastFourDigitsLabel: 'Last 4 digits',
        lastFourDigitsPlaceholder: '1234',
        closingDayLabel: 'Closing date',
        closingDayHint: 'The day the statement closes and new purchases roll to the next one.',
        dueDayLabel: 'Due date',
        dueDayHint: 'The day the statement is due.',
        dayValue: 'Day {{day}}',
        dueOnBusinessDayLabel: 'Due on business days',
        dueOnBusinessDayHint: 'When the due date falls on a weekend or national holiday, the app shows and reminds you on the next business day, like the bank does.',
        closingDayToNextInvoiceLabel: 'Purchases on the closing day go to the next statement',
        closingDayToNextInvoiceHint: 'Some banks work this way. When on, the statement closes the day before and anything you log on the closing day goes to the next one. Existing entries are not moved.',
        paymentAccountLabel: 'Payment account',
        paymentAccountPlaceholder: 'Select account',
        colorLabel: 'Color',
        creditLimitLabel: 'Credit limit',
        creditLimitOptional: 'Optional',
        details: {
            show: 'More options',
            hide: 'Hide options',
        },
        unarchiveAction: 'Unarchive card',
        archiveAction: 'Archive card',
        deleteAction: 'Delete card',
        createAccessibility: 'Create card',
        saveAccessibility: 'Save',
        colorSheetTitle: 'Choose a color',
        archiveDialog: {
            unarchiveMessage: 'The card will show up again in listings and in the overview.',
            archiveMessage: 'The card will be hidden, but statements and transactions already recorded will be kept.',
            unarchiveConfirm: 'Unarchive',
            archiveConfirm: 'Archive',
        },
        deleteDialog: {
            message: 'Deleting removes this card from the entire app: its statements, purchases and recurring charges disappear from every screen and report. Only what already debited your accounts stays in the account history. To keep the history, use Archive instead.',
            confirm: 'Delete everything',
        },
    },
    healthBadge: {
        reconciledOn: 'Reconciled with the bank on {{date}}',
        reconciledItems_one: '{{count}} item reconciled',
        reconciledItems_other: '{{count}} items reconciled',
        uncategorizedA11y_one: '{{count}} uncategorized item, tap to resolve',
        uncategorizedA11y_other: '{{count}} uncategorized items, tap to resolve',
        showingUncategorized: 'Showing uncategorized · see all',
        chipUncategorized_one: '{{count}} uncategorized',
        chipUncategorized_other: '{{count}} uncategorized',
    },
    timeline: {
        status: {
            paid: 'Statement paid',
            projected: 'Next statement',
            closed: 'Statement closed',
            partial: 'Partial statement',
            overdue: 'Overdue statement',
            open: 'Open statement',
            empty: 'No transactions',
        },
        dueDate: 'Due {{date}}',
        dueDateEmpty: 'Due -',
        purchasesRange: 'Purchases from {{from}} to {{to}}',
        purchasesUntil: 'Purchases until {{date}}',
        purchasesUntilEmpty: 'Purchases until -',
        periodTooltip: {
            purchasesFrom: 'Purchases from',
            closesOn: 'Closes on',
        },
    },
    payInvoice: {
        dateFallback: 'Date',
        payButton: 'Pay {{amount}}',
        back: 'Back',
        amountLabel: 'Payment amount',
        remainingHint: 'Statement · Remaining {{amount}}',
        totalLabel: 'Statement total',
        paidLabel: 'Already paid',
        minimumLabel: 'Minimum payment',
        belowMinimumWarning: 'Amount below the minimum — subject to revolving credit interest',
        debitAccountLabel: 'Account to debit',
        selectAccountPlaceholder: 'Select account...',
        remainder: {
            title: 'Remaining balance',
            summary: '{{amount}} left to clear the statement.',
            rollToNextLabel: 'Roll to next',
            rollToNextHint: 'Adds to the next statement as previous balance.',
            adjustLabel: 'Record a statement credit',
            adjustHint: 'Reduces the current statement by this amount and pays it off.',
            keepOpenLabel: 'Keep open',
            keepOpenHint: 'Leaves the balance pending on this statement.',
        },
        overpayment: {
            title: 'Overpayment',
            summary: "You're paying {{amount}} over the statement total.",
            creditToNextLabel: 'Credit to next statement',
            creditToNextHint: 'The next statement starts already reduced by this amount.',
            creditLabel: 'Record credit to account',
            creditHint: 'Returns the surplus to the account.',
            extraEntryLabel: 'Record adjustment on statement',
            extraEntryHint: 'Creates an extra item to reflect the amount.',
            lateFeeLabel: 'Record as interest/late fee',
            lateFeeHint: 'Records the surplus as interest and late fee on the statement.',
            ignoreLabel: 'Ignore',
            ignoreHint: 'Confirms the payment without any adjustment.',
        },
    },
    reconcile: {
        chooseCategory: 'Choose category',
        aiFill: 'Fill with AI · {{n}}',
        aiFillLoading: 'Filling…',
        aiFillA11y: 'Fill the {{n}} uncategorized lines with AI',
        aiFillDone_one: 'AI filled the missing line. Review before adding.',
        aiFillDone_other: 'AI filled {{n}} of {{count}} lines. Review before adding.',
        aiFillNone: 'AI could not identify any of these lines from the description.',
        aiFillFailed: 'Could not reach the AI right now. Try again.',
        aiConsentTitle: 'Fill with AI',
        aiConsentMessage: 'Kontto will send {{provider}} the description of the uncategorized lines and the names of your categories, using your key. Amount, date, account and card never leave the device. This applies to future statements too; you can revoke it in Settings › AI Assistant.',
        aiConsentConfirm: 'Send and fill',
        aiNoKeyTitle: 'Set up AI first',
        aiNoKeyMessage: 'To let AI fill categories, add your key in Settings › AI Assistant. What Kontto already filled stays as is.',
        aiNoKeyConfirm: 'Open settings',
        installment: 'Installment {{number}} · {{date}}',
        installmentWithTotal: 'Installment {{number}}/{{total}} · {{date}}',
        valueDiffTitle: 'Different amount ({{n}})',
        valueDiffHint: 'Same purchase, amount differs — select to fix in Kontto',
        valueDiffKontto: 'Kontto: {{amount}}',
        importTitle: 'Statement transactions ({{n}})',
        importHint: 'Review the category and confirm to record everything',
        missingTitle: 'Missing in Kontto ({{n}})',
        missingHint: 'Statement charges with no transaction — select to record',
        refundsTitle: 'Credits to record ({{n}})',
        refundsHint: 'Statement credits with no transaction. A refund offsets the purchase category; cashback is recorded as income and reduces the bill.',
        refundSubtitle: 'Refund · {{date}}',
        cashbackSubtitle: 'Cashback · {{date}}',
        creditModeRefund: 'Refund',
        creditModeCashback: 'Cashback',
        cashbackAccount: 'Goes to {{account}}',
        chooseAccount: 'Choose account',
        cashbackAccountSheetTitle: 'Account that receives the cashback',
        cashbackAccountMissingTitle: 'Choose the cashback account',
        cashbackAccountMissingBody: 'Cashback is recorded as income in an account. Tap "Choose account" on its row.',
        extraTitle: 'Only in Kontto ({{n}})',
        extraHint: 'Recorded in the app, not on this statement — check or delete',
        extraSeriesSubtitle: '{{date}} · installment/recurring',
        adjacentTitle: 'On the next invoice over ({{n}})',
        adjacentHint: 'Already in Kontto, but on another invoice — check to bring it into this one',
        adjacentSubtitle: '{{month}} invoice · {{date}}',
        adjacentSubtitleKontto: '{{month}} invoice · Kontto: {{amount}}',
        adjacentPaidSubtitle: 'On the {{month}} invoice, already paid — stays there',
        matchedItems_one: '{{count}} item reconciled',
        matchedItems_other: '{{count}} items reconciled',
        allReconciled: 'All reconciled! The bank statement matches what you recorded in Kontto.',
        passwordIncorrect: 'Incorrect password',
        readFailed: 'Failed to read the statement',
        readUnsupportedPdf: "This PDF uses a protection Kontto can't read yet. Try exporting the OFX from your internet banking.",
        readNoTextPdf: 'This PDF has no text layer — it looks scanned. Try importing screenshots instead.',
        readNoLinesPdf: 'We read the PDF but found no transactions. Try the OFX from your internet banking or screenshots of the statement.',
        readNoLinesImages: 'We found no transactions in these screenshots. Make sure each purchase shows its date and amount, then try again.',
        readEmptyFile: 'The file arrived empty. Open the PDF once in your bank app (or download it from Drive) and try again.',
        readEmptyOfxTitle: 'This OFX has no transactions',
        readEmptyOfx: "Check in your internet banking that you exported this card's statement and the right month.",
        readOfxCsv: "We couldn't recognize this CSV's columns — reconciliation needs date, description and amount. Export the statement as OFX or PDF and try again.",
        readOfxSpreadsheet: 'This looks like an Excel spreadsheet — reconciliation reads OFX, CSV or PDF. Export the statement as OFX from your internet banking.',
        readOfxImage: 'This file is an image — use the "Statement screenshots" option.',
        readOfxPdf: 'This file is a PDF — go back and use the "Statement PDF" option.',
        readOfxHtml: 'This file is a saved web page — reconciliation reads OFX, CSV or PDF. Export the statement as OFX from your internet banking.',
        readOfxBinary: 'Kontto did not recognize this file — reconciliation reads OFX, CSV or PDF. Export the statement as OFX from your internet banking.',
        readOfxNotOfx: 'This file is text, but it has no OFX content. In your internet banking, look for the OFX export (sometimes listed as "Money" or "Quicken").',
        appliedTitle: 'Statement reconciled',
        appliedMessage: '{{created}} recorded, {{corrected}} corrected.',
        appliedSeries_one: '{{count}} installment plan projected into upcoming invoices.',
        appliedSeries_other: '{{count}} installment plans projected into upcoming invoices.',
        appliedMoved_one: '{{count}} moved from the neighboring invoice.',
        appliedMoved_other: '{{count}} moved from the neighboring invoice.',
        appliedMoveSkipped_one: "{{count}} couldn't be moved.",
        appliedMoveSkipped_other: "{{count}} couldn't be moved.",
        projectSeriesTitle: 'Project future installments',
        projectSeriesSubtitle: 'Installment purchases become a series: the remaining installments go into upcoming invoices.',
        applyFailed: "Couldn't apply",
        deleteFailed: "Couldn't delete",
        pickHelp: 'Import your bank statement and Kontto compares it with what you already logged — all on your device, nothing leaves it.',
        freeUseBanner_one: 'You have <0>{{count}} free check</0>. After that, it becomes a Kontto Pro feature.',
        freeUseBanner_other: 'You have <0>{{count}} free checks</0>. After that, it becomes a Kontto Pro feature.',
        recommendedLabel: 'Recommended',
        pdfSourceTitle: 'Statement PDF',
        pdfSourceSubtitle: 'The bank file, with or without a password.',
        pdfSourceBadge: 'Most used',
        ofxSourceTitle: 'OFX or CSV file',
        ofxSourceSubtitle: 'Exported from your bank app or website, no OCR.',
        ofxSourceBadge: 'Exact reading',
        imagesSourceTitle: 'Statement screenshots',
        imagesSourceSubtitle: 'If you have no PDF, OFX or CSV — OCR may misread amounts.',
        optimizedForCount_one: 'Optimized for {{count}} bank',
        optimizedForCount_other: 'Optimized for {{count}} banks',
        banksPreview: 'Nubank, Itaú, Bradesco, Inter and more.',
        otherBanksNote: 'Other banks work too — check the items before applying.',
        pdfProtectedTitle: 'This PDF is protected',
        pdfProtectedSubtitle: 'Enter the statement password (usually the first digits of your CPF).',
        passwordLabel: 'PDF password',
        openInvoiceButton: 'Open statement',
        processing: 'Reading the statement and comparing…',
        importIntro_one: 'We found {{count}} transaction in this statement. Review each category and confirm to start with everything recorded.',
        importIntro_other: 'We found {{count}} transactions in this statement. Review each category and confirm to start with everything recorded.',
        summaryPurchases: 'Purchases on the statement',
        summaryRefunds: 'Credits',
        negativeAmount: '− {{amount}}',
        inKonttoToday: 'In Kontto today',
        checksumWarning: 'Heads up: some statement items may not have been read. Check before applying.',
        nothingToApply: 'Nothing to apply',
        applyCount_one: 'Apply {{count}} item',
        applyCount_other: 'Apply {{count}} items',
        deleteEntryTitle: 'Delete transaction',
        deleteSeriesMessage: '"{{description}}" is installment/recurring. What do you want to delete?',
        deleteSingleLabel: 'Only this one',
        deleteSingleDescription: 'Removes only this transaction.',
        deleteFutureLabel: 'This and future ones',
        deleteFutureDescription: 'Deletes this and future occurrences.',
        deleteAllLabel: 'All',
        deleteAllDescription: 'Deletes the whole series.',
        paidWarningTitle: 'Invoice already paid',
        paidWarningMessage: 'The {{month}} invoice is already paid. Import anyway?',
        paidWarningContinue: 'Continue',
        paidWarningBack: 'Go back',
        extrasPromptTitle: 'Match the statement?',
        extrasPromptMessage_one:
            '{{count}} transaction on this invoice in Kontto is not on the imported statement.',
        extrasPromptMessage_other:
            '{{count}} transactions on this invoice in Kontto are not on the imported statement.',
        extrasPromptDelete_one: 'Delete it and apply',
        extrasPromptDelete_other: 'Delete all {{count}} and apply',
        extrasPromptDeleteDescription:
            "The invoice will match the bank's. Installment/recurring: only this invoice's entry is removed.",
        extrasPromptKeep: 'Keep and apply',
        extrasPromptKeepDescription: 'Applies the rest and keeps those transactions.',
        extrasPromptDeleteOnly_one: 'Delete and finish',
        extrasPromptDeleteOnly_other: 'Delete all {{count}} and finish',
        deleteOnlyCount_one: 'Delete {{count}} item from the invoice',
        deleteOnlyCount_other: 'Delete {{count}} items from the invoice',
        extrasDeletedMessage_one: '{{count}} transaction deleted — invoice now matches the bank.',
        extrasDeletedMessage_other: '{{count}} transactions deleted — invoice now matches the bank.',
    },
    invoice: {
        newEntryAccessibility: 'Add to card',
        manualAdjustments: 'Manual adjustments',
        uncategorized: 'Uncategorized',
        originCurrentMonth: 'Bought this month',
        manualAdjustedEmpty: 'This statement amount was adjusted manually. There are no individual linked items yet.',
        emptyTitle: 'Your statement is empty',
        emptySubtitle: 'Import the bank PDF and start with everything recorded and already categorized.',
        importButton: 'Import bank statement',
        limitLabel: 'Limit',
        limitUsedRow: 'Used',
        limitUsedOf: '{{used}} of {{total}}',
        limitCardRow: 'Card limit',
        limitPocketRow: 'Backed by a pocket',
        limitPocketRowHint: 'the reserve adds to your available limit',
        limitPocketBoost: '+{{amount}}',
        paymentsPaidSummary: '{{amount}} paid',
        paymentsPaidInFullSingle: 'Paid in full',
        paymentsPaidInFull: 'Paid in full',
        paymentsPaidSingleMeta: '{{amount}} · {{date}}',
        paymentsPaidMultiMeta_one: '{{amount}} · {{count}} payment',
        paymentsPaidMultiMeta_other: '{{amount}} · {{count}} payments',
        paymentsCount_one: '{{count}} payment',
        paymentsCount_other: '{{count}} payments',
        paymentsCountRemaining_one: '{{count}} payment · {{remaining}} left',
        paymentsCountRemaining_other: '{{count}} payments · {{remaining}} left',
        noPaymentAccountTitle: 'Choose the account that pays this bill',
        noPaymentAccountBody: 'Without it, the bill counts in your projected balance but doesn\'t show up in any account\'s projection or in the transaction list.',
        payInvoiceButton: 'Pay statement',
        quickImport: 'Import',
        quickAdjustTotal: 'Adjust total',
        quickAnalysis: 'Analysis',
        quickReport: 'Report',
        lastPayment: 'Last payment',
        undoLastPayment: 'Undo last payment',
        undoPaymentA11y: 'Undo payment of {{amount}} on {{date}}',
        cancel: 'Cancel',
        selectedCount_one: '{{count}} selected',
        selectedCount_other: '{{count}} selected',
        selectAll: 'All',
        itemsTitle: 'Statement items',
        itemsInAccrual_one: '{{count}} item in this accrual',
        itemsInAccrual_other: '{{count}} items in this accrual',
        searchPlaceholder: 'Search by name or amount...',
        searchMatches: '{{count}} of {{total}} in this accrual',
        searchEmptyTitle: 'Nothing in this statement',
        searchEmptySubtitle: 'No item with that name or amount. Switch the month above to search another statement.',
        select: 'Select',
        tapToSelect: 'Tap the items to select',
        changeCategoryCount: 'Change category ({{n}})',
        removeAdjustment: 'Remove adjustment',
        confirmAdjustment: 'Confirm adjustment',
        adjustSheetTitle: 'Adjust statement amount',
        adjustAmountLabel: 'Statement total amount',
        analysisTitle: 'Statement analysis',
        analysisByCategory: 'Categories',
        analysisByTag: 'Tags',
        untagged: 'Untagged',
        topComposition: 'Top group',
        noItems: 'No items',
        notEnoughForAnalysis: 'Not enough items yet to analyze this statement.',
        activeInstallmentPlans: 'Active installment plans',
        installmentMonthly: '{{amount}}/mo · {{paid}}/{{total}}',
        simulateInstallment: 'Simulate installments',
        committedNextMonths: 'Committed in the coming months',
        undoPaymentDialogTitle: 'Undo payment',
        undoPaymentDialogMessage: 'Remove the payment of {{amount}} on {{date}}{{account}}?',
        undoPaymentAccountSuffix: ' made from the account {{account}}',
        undoConfirm: 'Undo',
        deleteInstallmentDialogTitle: 'Delete installment plan',
        deleteInstallmentDialogMessage: 'Delete "{{description}}" and all its installments?',
        deleteSelected: 'Delete selected',
        batchDeleteDialogTitle_one: 'Delete {{count}} entry',
        batchDeleteDialogTitle_other: 'Delete {{count}} entries',
        batchDeleteDialogMessage_one: 'The entry leaves the invoice and the total is recalculated. Invoice payments and adjustments are not affected.',
        batchDeleteDialogMessage_other: 'The entries leave the invoice and the total is recalculated. Invoice payments and adjustments are not affected.',
        batchDeleteInstallmentSeries_one: '{{count}} entry belongs to an installment purchase — the whole purchase will be deleted, including installments on other invoices.',
        batchDeleteInstallmentSeries_other: '{{count}} entries belong to installment purchases — the whole purchases will be deleted, including installments on other invoices.',
        batchDeleteInstallmentHint_one: 'The selection includes an installment entry: deleting removes the whole purchase, with all its installments.',
        batchDeleteInstallmentHint_other: 'The selection includes installment entries: deleting removes the whole purchases, with all their installments.',
    },
    report: {
        headerTitle: '{{name}} · {{period}}',
        totalFiltered: 'Filtered total',
        itemsLabel: 'Items',
        byTagTitle: 'Total by tag',
        untagged: 'Untagged',
        tagItemCount_one: '{{count}} item',
        tagItemCount_other: '{{count}} items',
        pdfColTag: 'Tag',
        filterByCategory: 'Categories',
        selectedCategoriesCount_one: '{{count}} selected',
        selectedCategoriesCount_other: '{{count}} selected',
        allCategories: 'All categories',
        noCategoriesInInvoice: 'No categories in this statement.',
        filterByTag: 'Tags',
        allTags: 'All tags',
        emptyFilter: 'No transactions match this filter.',
        uncategorized: 'Uncategorized',
        exportPdf: 'Export PDF',
        locked: {
            caption: 'Your report is ready',
            ctaTrial: 'See my report · {{period}} free',
            ctaPlans: 'See my report with Pro',
            trialTerms: 'Then {{price}}/year · cancel anytime',
            notNow: 'Not now',
            unlocked: 'Done: your report is unlocked',
        },
        exportError: 'Error exporting',
        filterCategorySingle: 'Category: {{name}}',
        filterCategoryMulti: '{{n}} categories',
        filterTagSingle: 'Tag: {{name}}',
        filterTagMulti: '{{n}} tags',
        pdfTitle: 'Statement {{name}}',
        pdfTotalLabel: 'Total',
        pdfSectionTitle: 'Transactions',
        pdfColData: 'Date',
        pdfColDescription: 'Description',
        pdfColCategory: 'Category',
        pdfColAmount: 'Amount',
    },
    selector: {
        title: 'Select card',
        allCards: 'All cards',
        addCard: 'Add card',
        limit: 'Limit: {{amount}}',
        emptyTitle: 'No cards registered',
        emptySubtitle: 'Add a card to link this purchase and continue.',
    },
    simulator: {
        title: 'Simulate installments',
        descriptionLabel: 'Description',
        descriptionPlaceholder: 'e.g. Laptop, Phone...',
        totalLabel: 'Total amount',
        installmentCountLabel: 'Number of installments',
        perInstallmentLabel: 'Amount per installment',
        perInstallmentValue: '{{installments}}x of {{amount}}',
        statementImpact: 'Impact per statement',
        moreMonths_one: '+{{count}} more month...',
        moreMonths_other: '+{{count}} more months...',
        confirm: 'Confirm installment plan',
    },
} satisfies DeepPartialStrings<TranslationSchema['creditCards']>;
